import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect
} from 'react';
import { ResizeObserver } from 'resize-observer'; // we use a resize-observer ponyfill because jest doesn't support this feature yet.

const passThroughCallback = cb => cb();

/**
 * @param {any} value
 * @param {object} options.blockPainting
 * @param {object} options.useRequestAnimationFrame
 * @returns array<isOverflow: boolean, refCallback: function>
 */
function useIsTextOverflow(
  value,
  options = { blockPainting: false, useRequestAnimationFrame: false }
) {
  const [isOverflow, setIsOverflow] = useState(false);
  const [elementRef, setElementRef] = useState(null);
  const [elementSize, setElementSize] = useState(0);
  const observerRef = useRef(null);

  const refCallback = useCallback(node => {
    if (node) {
      if (observerRef.current) {
        observerRef.current.disconnect(); // in case react triggers the ref callback again or for a different node
      }
      // we want to keep track on size change so we can re calculate the overflow
      observerRef.current = new ResizeObserver(entries => {
        const [element] = entries;
        setElementSize(element.contentRect.width);
      });
      setElementRef(node);
      observerRef.current.observe(node);
    }
  }, []);

  const useEffectType = options.blockPainting ? useLayoutEffect : useEffect;
  const executer = options.useRequestAnimationFrame
    ? requestAnimationFrame
    : passThroughCallback;

  useEffectType(() => {
    if (elementRef) {
      executer(() => {
        const { scrollWidth, clientWidth } = elementRef;
        const isOverflowing = scrollWidth > clientWidth;
        setIsOverflow(isOverflowing);
      });
    }
  }, [value, elementRef, elementSize]);

  useEffectType(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return [isOverflow, refCallback];
}

export default useIsTextOverflow;
